import React, { useCallback, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import { FormValidation } from "react-jsonschema-form";
import styled from "styled-components";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useChainData } from "../../components/chain-data-provider";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { useProperty } from "../../components/property-provider";
import actions from "../../lib/actions";
import { useUserAuthInfo } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { useApiClient, useHttpClient } from "../../lib/http";
import { EngineNavigationStyleType } from "../../gen/witbooking/backoffice/propertyconfiguration/v1/propertyconfiguration_api_pb";
import { regenPropertyCache } from "../common/api";
import {
  getEngineAppearance,
  updateEngineAppearance,
} from "../engineAppeareance/infrastructure/engineAppearance.api";
import CrudForm, { defaultFormMessages } from "../shared/crud/form";
import { BehaviourEntity, NavigationStyle } from "./domain/behaviour.entity";
import { buildFormConfiguration } from "./domain/behaviour.formConfiguration";
import schema from "./domain/behaviour.schema.json";
import { getBehaviour, updateBehaviour } from "./infrastructure/behaviour.api";
import { messages } from "./messages";

function toProtoNavigationStyle(value: NavigationStyle): EngineNavigationStyleType {
  return value === NavigationStyle.STEPPER
    ? EngineNavigationStyleType.STEPPER
    : EngineNavigationStyleType.SIMPLE;
}

export const FormContainer = styled("div")`
  .form-group.field.field-boolean {
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    justify-content: flex-end;
  }

  .form-group.field.field-boolean:has(.help-block) {
    flex-wrap: wrap;

    .help-block {
      width: 100%;
      order: 1;
      margin-top: 2px;
      font-size: 13px;
      color: #6c757d;
    }
  }

  select {
    width: 280px;
  }

  input[type="text"] {
    width: 280px;
  }
`;

function BehaviourForm({ propertyTicker }: { propertyTicker: string }) {
  const intl = useIntl();
  const propertyInfo = useProperty();
  const propertyConf = usePropertyConfiguration();
  const chainData = useChainData();
  const configuration = useConfig();
  const middlewareClient = useHttpClient(configuration.middlewareEndpoint);
  const apiClient = useApiClient();
  const userAuthInfo = useUserAuthInfo();

  const previousNavigationStyleRef = useRef<NavigationStyle | undefined>(undefined);

  const handleGetOne = useCallback(async () => {
    const entity = await getBehaviour(middlewareClient, propertyInfo.ticker);
    previousNavigationStyleRef.current = entity.navigationStyle;
    return entity;
  }, [middlewareClient, propertyInfo.ticker]);

  const handleUpdate: (entity: BehaviourEntity) => Promise<void> = useCallback(
    async entity => {
      await updateBehaviour(propertyInfo.ticker, entity, middlewareClient);

      if (previousNavigationStyleRef.current !== entity.navigationStyle) {
        const engineAppearance = await getEngineAppearance();
        const protoNavigationStyle = toProtoNavigationStyle(entity.navigationStyle);
        await updateEngineAppearance({
          ...engineAppearance,
          mobilenavigationstyle: protoNavigationStyle,
          desktopnavigationstyle: protoNavigationStyle,
        });
        previousNavigationStyleRef.current = entity.navigationStyle;
      }

      await regenPropertyCache(apiClient, propertyInfo.ticker);
      alert(intl.formatMessage(messages.saveSuccess));
      return;
    },
    [apiClient, intl, middlewareClient, propertyInfo.ticker]
  );

  const customValidate = useCallback(
    (formData: BehaviourEntity, errors: FormValidation) => {
      const reelItems = formData.engineHighlightReelOrder?.split(",").filter(Boolean) ?? [];
      if (reelItems.length > 0 && reelItems.length < 4) {
        errors.engineHighlightReelOrder.addError(
          intl.formatMessage(messages.highlightReelMinExtras)
        );
      }

      if (!formData.groupBookingModuleSection) {
        return errors;
      }

      const {
        groupBookingModule,
        groupBookingMinGuests,
        groupBookingMaxGuests,
      } = formData.groupBookingModuleSection;
      if (groupBookingModule) {
        if (!groupBookingMinGuests) {
          // @ts-ignore
          errors.groupBookingModuleSection.groupBookingMinGuests.addError(
            intl.formatMessage(defaultFormMessages.requiredValidationFailed)
          );
          errors.groupBookingMinGuests.addError(
            intl.formatMessage(defaultFormMessages.requiredValidationFailed)
          );
          // @ts-ignore
          errors.groupBookingModuleSection.groupBookingMinGuests["__errors"] = [
            intl.formatMessage(defaultFormMessages.requiredValidationFailed),
          ];
        }
        if (!groupBookingMaxGuests) {
          // @ts-ignore
          errors.groupBookingModuleSection.groupBookingMaxGuests.addError(
            intl.formatMessage(defaultFormMessages.requiredValidationFailed)
          );
          errors.groupBookingMaxGuests.addError(
            intl.formatMessage(defaultFormMessages.requiredValidationFailed)
          );
          // @ts-ignore
          errors.groupBookingModuleSection.groupBookingMaxGuests["__errors"] = [
            intl.formatMessage(defaultFormMessages.requiredValidationFailed),
          ];
        }
        if (
          groupBookingMinGuests &&
          groupBookingMaxGuests &&
          groupBookingMaxGuests <= groupBookingMinGuests
        ) {
          // @ts-ignore
          errors.groupBookingModuleSection.groupBookingMaxGuests.addError(
            intl.formatMessage(messages.mustBeGreaterThanMinGuests)
          );
          errors.groupBookingMaxGuests.addError(
            intl.formatMessage(messages.mustBeGreaterThanMinGuests)
          );
          // @ts-ignore
          errors.groupBookingModuleSection.groupBookingMaxGuests["__errors"] = [
            intl.formatMessage(messages.mustBeGreaterThanMinGuests),
          ];
        }
      }

      return errors;
    },
    [intl]
  );

  const isChain = chainData?.isChain ?? false;

  const formConfiguration = useMemo(
    () => buildFormConfiguration(propertyConf, userAuthInfo?.isWitAdmin, isChain),
    [propertyConf, userAuthInfo?.isWitAdmin, isChain]
  );

  const effectiveSchema = useMemo(() => {
    if (!isChain) return schema;
    const { useChildEngineCancelPolicyOrderInsteadAgr: _omit, ...rest } = schema.properties;
    return { ...schema, properties: rest };
  }, [isChain]);

  return (
    <CrudForm<BehaviourEntity>
      id={"behaviourEntity"}
      schema={effectiveSchema}
      formConfiguration={formConfiguration}
      messages={messages}
      onGet={handleGetOne}
      onUpdate={handleUpdate}
      canUpdate={actions.ModifyAppeareanceSettings}
      enumsOptional
      container={FormContainer}
      validate={customValidate}
    />
  );
}

export default withAuthorizationVerifier(BehaviourForm);
